import { observable, syncState } from "@legendapp/state";
import { useValue } from "@legendapp/state/react";
import { Icon } from "@repo/ui/components/icon";
import { ScrollFade } from "@repo/ui/components/scroll-fade";
import { useSidebarMobileClick } from "@repo/ui/hooks/use-sidebar-mobile-click";
import { Input } from "@repo/ui/input";
import {
  SidebarMenu,
  SidebarMenuAction,
  SidebarMenuButton,
  SidebarMenuItem,
} from "@repo/ui/ui/sidebar";
import { Skeleton } from "@repo/ui/ui/skeleton";
import { createLink } from "@tanstack/react-router";
import { useRef, useState } from "react";

import { chat$ } from "~/stores/chat";
import { threads$ } from "~/stores/threads";
import { ThreadOptions } from "./thread-options";

const SidebarMenuButtonLink = createLink(SidebarMenuButton);

/**
 * Recent non-favorited threads, newest first. Favorited threads move to the
 * dedicated Favorites section, mirroring boards/datasets.
 */
const sortedThreadIds$ = observable<string[]>(() => {
  const map = threads$.get() ?? {};
  return Object.values(map)
    .filter((thread) => !thread.metadata?.favorite)
    .sort(
      (a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
    )
    .map((thread) => thread.id);
});

export const ThreadsList = () => {
  const isLoading = useValue(() => !syncState(threads$).isLoaded.get());
  const threadIds = useValue(sortedThreadIds$);

  if (isLoading) {
    return <Skeleton className="h-8 w-full" />;
  }

  if (threadIds.length === 0) {
    return (
      <p className="px-2 pb-2 text-sidebar-foreground/70 text-xs">
        Ask the AI agent anything to start a thread.
      </p>
    );
  }

  return (
    <ScrollFade
      className="no-scrollbar -m-1 max-h-50 p-1"
      fromClassName="from-sidebar"
    >
      <SidebarMenu>
        {threadIds.map((id) => (
          <ThreadItem key={id} id={id} />
        ))}
      </SidebarMenu>
    </ScrollFade>
  );
};

export const ThreadItem = ({ id }: { id: string }) => {
  const handleLinkClick = useSidebarMobileClick("left");
  const title = useValue(threads$[id].title);
  const isStreaming = useValue(chat$.isStreaming);

  const [isRenaming, setIsRenaming] = useState(false);
  const renameOriginalTitleRef = useRef<string>("");

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    // While renaming, the inline input swaps in for the label; guard against a
    // stray click on the row navigating away mid-rename.
    if (isRenaming) {
      e.preventDefault();
      return;
    }
    // Navigating mid-stream unmounts the streaming Chat instance and drops the
    // in-flight turn; block the navigation until the agent finishes.
    if (isStreaming) {
      e.preventDefault();
      return;
    }
    handleLinkClick();
  };

  const startRename = () => {
    renameOriginalTitleRef.current = title ?? "";
    setIsRenaming(true);
  };

  const handleDoubleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    startRename();
  };

  const handleRenameKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.currentTarget.blur();
    }
    if (e.key === "Escape") {
      const currentValue = e.currentTarget.value.trim();
      if (currentValue === "") {
        threads$[id].title.set(renameOriginalTitleRef.current);
      }
      e.currentTarget.blur();
    }
  };

  return (
    <ThreadOptions type="context" threadId={id} onRename={startRename}>
      <SidebarMenuItem>
        <SidebarMenuButtonLink
          name="left"
          to="/thread/$threadId"
          params={{ threadId: id }}
          activeProps={{ isActive: true }}
          onClick={handleClick}
          onDoubleClick={handleDoubleClick}
        >
          <Icon name="comment" />
          {isRenaming ? (
            <Input
              value={title ?? ""}
              onChange={(e) => threads$[id].title.set(e.target.value)}
              onBlur={() => setIsRenaming(false)}
              onKeyDown={handleRenameKeyDown}
              placeholder="New thread"
              className="h-auto min-w-0 flex-1 rounded-none border-0 bg-transparent p-0 text-sm shadow-none focus-visible:border-0 focus-visible:shadow-none focus-visible:ring-0 dark:bg-transparent"
              autoFocus
              onClick={(e) => e.stopPropagation()}
              onDoubleClick={(e) => e.stopPropagation()}
            />
          ) : (
            <span className="truncate">{title || "New thread"}</span>
          )}
        </SidebarMenuButtonLink>
        <ThreadOptions threadId={id} onRename={startRename}>
          <SidebarMenuAction
            showOnHover
            className="rounded-sm data-[state=open]:bg-accent"
          >
            <Icon name="more-horizontal" />
            <span className="sr-only">More</span>
          </SidebarMenuAction>
        </ThreadOptions>
      </SidebarMenuItem>
    </ThreadOptions>
  );
};

/**
 * Read-only "you are here" stub for the active thread, rendered under the
 * Threads section header when the section is collapsed.
 */
export const ActiveThreadItem = ({ id }: { id: string }) => {
  const title = useValue(threads$[id].title);

  return (
    <SidebarMenuItem>
      <SidebarMenuButtonLink
        name="left"
        to="/thread/$threadId"
        params={{ threadId: id }}
        activeProps={{ isActive: true }}
      >
        <Icon name="comment" />
        <span className="truncate">{title || "New thread"}</span>
      </SidebarMenuButtonLink>
    </SidebarMenuItem>
  );
};
